import React, { useState } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { routes } from "../routes";
import userService from "../../services/userService";
import { IRegisterRequest } from "../../interfaces/user";
import JF_LOGO from "../../assets/images/JF Horizontal Color.png";

const SignupSchema = Yup.object().shape({
  firstName: Yup.string().required("First name is required"),
  lastName: Yup.string().required("Last name is required"),
  email: Yup.string().email("Invalid email address").required("Email is required"),
  company: Yup.string().required("Company name is required"),
  jobtitle: Yup.string().required("Job title is required"),
  password: Yup.string().min(8, "Password must be at least 8 characters").required("Password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password")], "Passwords must match")
    .required("Please confirm your password"),
});

interface IProps {
  register: (request: IRegisterRequest) => Promise<any>;
}

const DeveloperPortalSignup: React.FC<IProps> = ({ register }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (values: any) => {
    setIsSubmitting(true);
    try {
      await register({
        ...values,
        companyType: "DEVELOPER",
        city: "N/A",
        state: "N/A",
        country: "N/A",
        phoneNumber: "N/A",
      });
      setIsSuccess(true);
    } catch (err) {
      console.error("Registration failed", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="developer-portal-auth">
        <div className="auth-card" style={{ textAlign: "center" }}>
          <div className="brand-logo">
            <img src={JF_LOGO} alt="Journey Foods" />
          </div>
          <div className="auth-header">
            <div style={{ marginBottom: "24px" }}>
               <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="32" cy="32" r="32" fill="#ECFDF5"/>
                <path d="M44 24L28 40L20 32" stroke="#10B981" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h1>Verify your email</h1>
            <p>We've sent a secure link to <strong>your inbox</strong>. Please click the link to activate your developer account.</p>
          </div>
          <Link to={routes.DEVELOPER_PORTAL_LOGIN} className="auth-submit-btn" style={{ textDecoration: 'none', display: 'block' }}>
            Return to Sign In
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="developer-portal-auth">
      <div className="auth-card" style={{ maxWidth: "540px" }}>
        <div className="brand-logo">
          <img src={JF_LOGO} alt="Journey Foods" />
        </div>

        <div className="auth-header">
          <h1>Create Developer Account</h1>
          <p>Join 2,000+ developers building the future of food science with the Journey AI API.</p>
        </div>

        <Formik
          initialValues={{
            firstName: "",
            lastName: "",
            email: "",
            company: "",
            jobtitle: "",
            password: "",
            confirmPassword: "",
          }}
          validationSchema={SignupSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, isValid, dirty }) => (
            <Form className="auth-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">First Name</label>
                  <div className="input-wrapper">
                    <Field name="firstName" placeholder="Jane" className={errors.firstName && touched.firstName ? "is-invalid" : ""} />
                  </div>
                  <ErrorMessage name="firstName" component="div" className="error-message" />
                </div>
                <div className="form-group">
                  <label htmlFor="lastName">Last Name</label>
                  <div className="input-wrapper">
                    <Field name="lastName" placeholder="Doe" className={errors.lastName && touched.lastName ? "is-invalid" : ""} />
                  </div>
                  <ErrorMessage name="lastName" component="div" className="error-message" />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="email">Work Email Address</label>
                <div className="input-wrapper">
                  <Field type="email" name="email" placeholder="jane@company.com" className={errors.email && touched.email ? "is-invalid" : ""} />
                </div>
                <ErrorMessage name="email" component="div" className="error-message" />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="company">Company Name</label>
                  <div className="input-wrapper">
                    <Field name="company" placeholder="Acme Inc." className={errors.company && touched.company ? "is-invalid" : ""} />
                  </div>
                  <ErrorMessage name="company" component="div" className="error-message" />
                </div>
                <div className="form-group">
                  <label htmlFor="jobtitle">Job Title</label>
                  <div className="input-wrapper">
                    <Field name="jobtitle" placeholder="Developer" className={errors.jobtitle && touched.jobtitle ? "is-invalid" : ""} />
                  </div>
                  <ErrorMessage name="jobtitle" component="div" className="error-message" />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <div className="input-wrapper">
                  <Field type="password" name="password" placeholder="••••••••" className={errors.password && touched.password ? "is-invalid" : ""} />
                </div>
                <ErrorMessage name="password" component="div" className="error-message" />
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <div className="input-wrapper">
                  <Field type="password" name="confirmPassword" placeholder="••••••••" className={errors.confirmPassword && touched.confirmPassword ? "is-invalid" : ""} />
                </div>
                <ErrorMessage name="confirmPassword" component="div" className="error-message" />
              </div>

              <button
                type="submit"
                className="auth-submit-btn"
                disabled={!isValid || !dirty || isSubmitting}
              >
                {isSubmitting ? (
                  <span>
                    <i className="fa fa-spinner fa-spin spinner"></i> Initializing Portal...
                  </span>
                ) : (
                  "Create Developer Account"
                )}
              </button>
            </Form>
          )}
        </Formik>

        <div className="auth-footer">
          Already have an account? <Link to={routes.DEVELOPER_PORTAL_LOGIN}>Log in here</Link>
        </div>
      </div>
    </div>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  register: (request: IRegisterRequest) => dispatch(userService.register(request)),
});

export default connect(null, mapDispatchToProps)(DeveloperPortalSignup);
